import { PaginatedListAspectRatioHelper } from "@theme/paginated-list-aspect-ratio";
import { sectionRenderer } from "@theme/section-renderer";

/**
 * Loads the next collection page and appends its product cards.
 */
class LnLoadMore extends HTMLElement {
  /** @type {PaginatedListAspectRatioHelper | null} */
  #aspectRatioHelper = null;

  connectedCallback() {
    this.addEventListener("click", this.#handleClick);

    const templateCard = this.closest("results-list")?.querySelector(
      '[ref="cardGallery"]',
    );
    if (templateCard instanceof HTMLElement) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleClick);
  }

  #handleClick = async (event) => {
    if (!(event.target instanceof Element) || !event.target.closest("button"))
      return;

    const button = this.querySelector("button");
    const status = this.querySelector('[role="status"]');
    const resultsList = this.closest("results-list");
    const grid = resultsList?.querySelector('[ref="grid"]');
    const sectionId = this.getAttribute("section-id");

    if (
      !(button instanceof HTMLButtonElement) ||
      !(grid instanceof HTMLElement) ||
      !sectionId
    )
      return;

    const lastCard = grid.querySelector(':scope > [ref="cards[]"]:last-child');
    const currentPage = Number(
      lastCard instanceof HTMLElement ? lastCard.dataset.page : 0,
    );
    const lastPage = Number(grid.dataset.lastPage);
    const nextPage = currentPage + 1;

    if (!currentPage || !lastPage || nextPage > lastPage) {
      this.hidden = true;
      return;
    }

    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    if (status) status.textContent = "";

    try {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set("page", String(nextPage));
      nextUrl.searchParams.delete("section_id");
      nextUrl.hash = "";
      const historyUrl = new URL(nextUrl);

      const pageHTML = await sectionRenderer.getSectionHTML(
        sectionId,
        true,
        nextUrl,
      );
      const parsedPage = new DOMParser().parseFromString(pageHTML, "text/html");
      const nextGrid = parsedPage.querySelector('[ref="grid"]');
      const nextCards = nextGrid?.querySelectorAll(':scope > [ref="cards[]"]');

      if (!nextCards?.length)
        throw new Error("The next product page is empty.");
      if (!this.isConnected) return;

      grid.append(...nextCards);
      this.#aspectRatioHelper?.processNewElements();
      history.pushState("", "", historyUrl);

      if (status) status.textContent = this.dataset.loadedMessage ?? "";
      if (nextPage >= lastPage) this.hidden = true;
    } catch {
      if (status) status.textContent = this.dataset.errorMessage ?? "";
    } finally {
      if (this.isConnected) {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      }
    }
  };
}

if (!customElements.get("ln-load-more")) {
  customElements.define("ln-load-more", LnLoadMore);
}
